import type { DayCourtReschedulePayload } from '@/components/schedule/DayCourtScheduleView';
import { lessonOverrideService } from '@/services/lessonOverride';
import { haveSameIdSet } from '@/utils/dayScheduleColumnDrag';
import { resolveOccurrenceCoachIds } from '@/utils/scheduleOccurrences';

export function hasDayScheduleTimeChange(payload: DayCourtReschedulePayload): boolean {
  return (
    payload.startTime !== payload.occurrence.startTime ||
    payload.endTime !== payload.occurrence.endTime
  );
}

export function hasDayScheduleCoachChange(payload: DayCourtReschedulePayload): boolean {
  if (payload.columnAxis !== 'coach' || !payload.coachIds) return false;
  return !haveSameIdSet(payload.coachIds, resolveOccurrenceCoachIds(payload.occurrence));
}

export function hasDayScheduleCourtChange(payload: DayCourtReschedulePayload): boolean {
  if (payload.columnAxis !== 'court') return false;
  const currentCourtIds = payload.occurrence.resolvedCourtIds ?? [];
  return !haveSameIdSet(payload.courtIds, currentCourtIds);
}

export async function commitDayScheduleDrag(params: {
  payload: DayCourtReschedulePayload;
  academyId: string;
  notifyGuardians?: boolean;
  reason?: string;
}): Promise<void> {
  const { payload, academyId, notifyGuardians, reason } = params;
  const sessionId = payload.occurrence.sessionId;
  if (!sessionId) {
    throw new Error('This session cannot be updated yet');
  }

  const timeChanged = hasDayScheduleTimeChange(payload);
  const courtChanged = hasDayScheduleCourtChange(payload);
  const coachChanged = hasDayScheduleCoachChange(payload);

  if (!timeChanged && !courtChanged && !coachChanged) {
    throw new Error('No schedule changes to save');
  }

  if (timeChanged || courtChanged) {
    await lessonOverrideService.rescheduleSession({
      academyId,
      sessionId,
      startTime: payload.startTime,
      endTime: payload.endTime,
      courtIds:
        payload.columnAxis === 'coach'
          ? payload.occurrence.resolvedCourtIds ?? []
          : payload.courtIds,
      sourceCourtId: payload.columnAxis === 'court' ? payload.sourceCourtId : undefined,
      reason,
      notifyGuardians,
    });
  }

  if (coachChanged && payload.coachIds) {
    await lessonOverrideService.replaceSessionCoaches({
      academyId,
      sessionId,
      coachIds: payload.coachIds,
      reason,
    });
  }
}
